import { DocumentTemplate } from "@dune/types";
import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@dune/ui";
import React, { useState } from "react";
import { toast } from "sonner";
import { useGenerateDocument } from "../../../../api/mutations/documents.mutations";
import {
	getEntitySelectionPlaceholder,
	getEntityTypeLabel,
	useEntityOptions,
} from "../../../../utils/entityHooks";

interface TestGenerationDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	template: DocumentTemplate;
}

const TestGenerationDialog: React.FC<TestGenerationDialogProps> = ({
	open,
	onOpenChange,
	template,
}) => {
	const [selectedEntityId, setSelectedEntityId] = useState<string>("");
	const [search, setSearch] = useState<string>("");
	const { options, isLoading } = useEntityOptions(template.entityModel, search);
	const generateDocument = useGenerateDocument();

	const handleGenerate = async () => {
		if (!selectedEntityId) {
			toast.error(
				`Veuillez sélectionner une ${getEntityTypeLabel(template.entityModel).toLowerCase()}`,
			);
			return;
		}

		generateDocument.mutate(
			{
				entityModel: template.entityModel,
				entityId: selectedEntityId,
				templateId: template.id,
			},
			{
				onSuccess: (data) => {
					toast.success("Document généré avec succès");
					window.open(data.documentUrl, "_blank");
					onOpenChange(false);
				},
				onError: (error) => {
					toast.error(`Erreur lors de la génération : ${error.message}`);
				},
			},
		);
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>Tester la génération</DialogTitle>
					<DialogDescription>
						Sélectionnez un{" "}
						{getEntityTypeLabel(template.entityModel).toLowerCase()} pour tester
						la génération du modèle "{template.name}"
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4 py-4">
					<div className="space-y-2">
						<Input
							type="text"
							placeholder="Filtrer par recherche textuelle"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
						<label htmlFor="entity-select" className="text-sm font-medium">
							{getEntityTypeLabel(template.entityModel)}
						</label>

						<Select
							value={selectedEntityId}
							onValueChange={setSelectedEntityId}
							disabled={isLoading}
						>
							<SelectTrigger id="entity-select">
								<SelectValue
									placeholder={
										isLoading
											? "Chargement..."
											: getEntitySelectionPlaceholder(template.entityModel)
									}
								/>
							</SelectTrigger>
							<SelectContent>
								{options.map((option) => (
									<SelectItem key={option.id} value={option.value}>
										<div className="flex flex-col">
											<span>{option.label}</span>
											{option.subtitle && (
												<span className="text-xs text-gray-500">
													{option.subtitle}
												</span>
											)}
										</div>
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					{selectedEntityId && (
						<div className="rounded-lg bg-blue-50 p-3">
							<p className="text-sm text-blue-800">
								Le document sera généré avec les données de{" "}
								{getEntityTypeLabel(template.entityModel).toLowerCase()}{" "}
								{template.entityModel === "SOCIETY" ||
								template.entityModel === "PROPOSITION"
									? "sélectionnée"
									: "sélectionné"}
								.
							</p>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Annuler
					</Button>
					<Button
						onClick={handleGenerate}
						disabled={!selectedEntityId || generateDocument.isPending}
						isLoading={generateDocument.isPending}
					>
						Générer le test
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
};

export default TestGenerationDialog;
